import { useEffect, useState } from "react";
import { supabase } from "../lib/supabase";
import { dayKeyOf } from "../lib/days";
import {
  CARRY_COMMENT_SELECT, CARRY_SESSION_SELECT, CARRY_UPCOMING_SELECT, carryAssignments, carryComments, carryDays,
  carryReviewSessions, carrySessions, saidByAssignment, saidByRow,
  type CommentRowIn, type Said, type SessionRowIn, type UpcomingRowIn,
} from "../lib/carryFeedback";

/** Row id → the exercise comment (A); assignment id → the workout's review (B). */
export type CarryFeedback = { readonly rows: ReadonlyMap<string, Said>; readonly workouts: ReadonlyMap<string, Said> };
const EMPTY: CarryFeedback = { rows: new Map(), workouts: new Map() };

type LineageRow = { id: string; workout_id: string; root_workout_id: string | null };

/**
 * THE COACH'S LAST FEEDBACK, WHERE THE CLIENT WILL MEET IT (`docs/CARRY-FEEDBACK-PLAN.md`) — for the Week's Extended
 * columns: (A) under an exercise, the coach's last comment on it; (B) under a workout's description, the coach's last review
 * of the same workout. The phone's own rules (`coachCommentFor`, `reviewCarryFor`), each on its FIRST coming place.
 *
 * Reads, each the coach's own (the two parties read `coach_messages`; the coach reads an active client's sessions and
 * assignments): the client's comments and reviews; their workouts' days; the client's sessions since two days before the
 * earliest of them (the phone's window) plus the reviewed sessions by id; the client's assignments from TODAY to the last
 * visible day (a place may sit between today and the visible range); and the lineage of every assignment a session names.
 * A failed read draws nothing — the lines are an aid, never a claim of absence.
 */
export function useCarryFeedback(clientId: string, lastDayKey: string | null, enabled: boolean): CarryFeedback {
  const [said, setSaid] = useState<CarryFeedback>(EMPTY);
  const key = enabled && lastDayKey !== null ? `${clientId}|${lastDayKey}` : "";
  useEffect(() => {
    if (key === "" || lastDayKey === null) { setSaid(EMPTY); return; }
    let cancelled = false;
    (async () => {
      const today = dayKeyOf(new Date());
      if (lastDayKey < today) { setSaid(EMPTY); return; }
      const m = await supabase.from("coach_messages").select(CARRY_COMMENT_SELECT).eq("client_id", clientId).in("kind", ["comment", "review"]);
      if (cancelled) return;
      const all = (m.data ?? []) as (CommentRowIn & { kind?: string })[];
      if (m.error !== null || all.length === 0) { setSaid(EMPTY); return; }
      const comments = all.filter((r) => r.kind === "comment");
      const reviews = all.filter((r) => r.kind === "review");
      const named = [...new Set(all.flatMap((r) => (r.session_id === null ? [] : [r.session_id])))];
      const reviewed = [...new Set(reviews.flatMap((r) => (r.session_id === null ? [] : [r.session_id])))];
      const earliest = all.map((r) => Date.parse(r.created_at)).reduce((a, b) => Math.min(a, b));
      const none = Promise.resolve({ data: [] as unknown[], error: null });
      const [d, s, sr, u] = await Promise.all([
        named.length === 0 ? none : supabase.from("workout_sessions").select("id, scheduled_date").in("id", named),
        supabase.from("workout_sessions").select(CARRY_SESSION_SELECT).eq("user_id", clientId)
          .gte("created_at", new Date(earliest - 2 * 86_400_000).toISOString()),
        reviewed.length === 0 ? none : supabase.from("workout_sessions").select(CARRY_SESSION_SELECT).in("id", reviewed),
        supabase.from("coach_assignments").select(CARRY_UPCOMING_SELECT).eq("client_id", clientId)
          .gte("scheduled_date", today).lte("scheduled_date", lastDayKey),
      ]);
      if (cancelled) return;
      if (d.error !== null || s.error !== null || sr.error !== null || u.error !== null) { setSaid(EMPTY); return; }
      const byId = new Map<string, SessionRowIn>();
      for (const row of [...((s.data ?? []) as SessionRowIn[]), ...((sr.data ?? []) as SessionRowIn[])]) byId.set(row.id, row);
      const sessions = [...byId.values()];
      const links = [...new Set(sessions.flatMap((x) => (x.coach_assignment_id == null ? [] : [x.coach_assignment_id])))];
      const l = links.length === 0 ? { data: [] as LineageRow[], error: null }
        : await supabase.from("coach_assignments").select("id, workout_id, root_workout_id").in("id", links);
      if (cancelled) return;
      if (l.error !== null) { setSaid(EMPTY); return; }
      const lineageOf = new Map(((l.data ?? []) as LineageRow[]).map((a) => [a.id, a.root_workout_id ?? a.workout_id] as const));
      const days = new Map(((d.data ?? []) as { id: string; scheduled_date: string | null }[])
        .flatMap((r) => (r.scheduled_date === null ? [] : [[r.id, r.scheduled_date] as const])));
      const upcoming = (u.data ?? []) as UpcomingRowIn[];
      const now = Date.now();
      setSaid({
        rows: saidByRow(carryDays(upcoming), carryComments(comments, days), carrySessions(sessions), today, now),
        workouts: saidByAssignment(carryAssignments(upcoming),
          reviews.map((r) => ({ id: r.id, sessionId: r.session_id, createdAt: r.created_at, body: r.body,
            dayKey: r.session_id === null ? null : (days.get(r.session_id)?.slice(0, 10) ?? null) })),
          carryReviewSessions(sessions, lineageOf), today, now),
      });
    })();
    return () => { cancelled = true; };
  }, [key, clientId, lastDayKey]);
  return said;
}
